import { useState, type FC } from 'react';
import { RotateCcw } from 'lucide-react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { messages } from '../localization/messages.ts';

export interface AgentLogo {
  src: string;
  alt: string;
}

/** Full-width brand bar; the mark links to the business while restart stays available separately. */
export const StageHeader: FC<{
  logo?: AgentLogo;
  logoHref?: string;
  headerBrandName?: string;
  brandDescriptor?: MessageDescriptor;
  contactLabel?: MessageDescriptor;
  contactHref?: string;
  onRestart: (intent: string) => void;
  disabled?: boolean;
}> = ({
  logo,
  logoHref,
  headerBrandName,
  brandDescriptor,
  contactLabel,
  contactHref,
  onRestart,
  disabled = false,
}) => {
  const intl = useIntl();
  const [failedSrc, setFailedSrc] = useState<string>();
  const displayName = headerBrandName || logo?.alt || '';
  const descriptor = brandDescriptor ? intl.formatMessage(brandDescriptor) : undefined;
  const contactText = contactLabel ? intl.formatMessage(contactLabel) : undefined;
  const showLogo = Boolean(logo?.src && logo.src !== failedSrc);

  if (!displayName && !showLogo) {
    return null;
  }

  return (
    <header className="stage-header jfy-header">
      <div className="jfy-header-inner">
        <div className="jfy-header-identity">
          {showLogo && logo ? (
            logoHref ? (
              <a
                href={logoHref}
                target="_blank"
                rel="noopener noreferrer"
                className="jfy-header-mark jfy-on-ink"
                aria-label={logo.alt}
              >
                <img
                  key={logo.src}
                  src={logo.src}
                  alt={logo.alt}
                  width={40}
                  height={56}
                  className="jfy-stamp-image"
                  onError={() => setFailedSrc(logo.src)}
                />
              </a>
            ) : (
              <button
                type="button"
                aria-label={intl.formatMessage(messages.brandStartOver, { brandName: logo.alt })}
                disabled={disabled}
                onClick={() => onRestart(intl.formatMessage(messages.startOver))}
                className="jfy-header-mark jfy-on-ink"
              >
                <img
                  key={logo.src}
                  src={logo.src}
                  alt={logo.alt}
                  width={40}
                  height={56}
                  className="jfy-stamp-image"
                  onError={() => setFailedSrc(logo.src)}
                />
              </button>
            )
          ) : null}
          <div className="jfy-header-copy">
            {displayName ? (
              <p className="jfy-header-title" translate="no">
                {displayName}
              </p>
            ) : null}
            {descriptor ? <p className="jfy-header-descriptor">{descriptor}</p> : null}
          </div>
        </div>
        <div className="jfy-header-actions">
          {logoHref ? (
            <button
              type="button"
              className="jfy-header-restart jfy-on-ink"
              aria-label={intl.formatMessage(messages.startOver)}
              title={intl.formatMessage(messages.startOver)}
              disabled={disabled}
              onClick={() => onRestart(intl.formatMessage(messages.startOver))}
            >
              <RotateCcw size={18} aria-hidden="true" />
            </button>
          ) : null}
          {contactHref && contactText ? (
            <a href={contactHref} className="jfy-contact-button jfy-on-ink">
              {contactText}
            </a>
          ) : null}
        </div>
      </div>
    </header>
  );
};
